import { useState } from "react";
import {
  Calendar,
  Clock,
  User,
  MapPin,
  Video,
  Phone,
  Monitor,
  Building2,
  Mail,
  Timer,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";

const InterviewDetails = ({ interview, onClose }) => {
  const { toast } = useToast();

  const getStatusColor = (status) => {
    const colors = {
      en_attente: "bg-orange-100 text-orange-800",
      confirme: "bg-blue-100 text-blue-800",
      en_cours: "bg-purple-100 text-purple-800",
      termine: "bg-green-100 text-green-800",
      reporte: "bg-yellow-100 text-yellow-800",
      annule: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      en_attente: "En attente",
      confirme: "Confirmé",
      en_cours: "En cours",
      termine: "Terminé",
      reporte: "Reporté",
      annule: "Annulé",
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      en_attente: <Clock className="h-4 w-4" />,
      confirme: <CheckCircle className="h-4 w-4" />,
      en_cours: <AlertCircle className="h-4 w-4" />,
      termine: <CheckCircle className="h-4 w-4" />,
      reporte: <Clock className="h-4 w-4" />,
      annule: <XCircle className="h-4 w-4" />,
    };
    return icons[status] || <Clock className="h-4 w-4" />;
  };

  const getResultatColor = (resultat) => {
    const colors = {
      positif: "bg-green-100 text-green-800",
      negatif: "bg-red-100 text-red-800",
      en_attente: "bg-gray-100 text-gray-800",
    };
    return colors[resultat] || "bg-gray-100 text-gray-800";
  };

  const getResultatLabel = (resultat) => {
    const labels = {
      positif: "Positif",
      negatif: "Négatif",
      en_attente: "En attente",
    };
    return labels[resultat] || "Non évalué";
  };

  const getPlatformDetails = (type) => {
    const details = {
      zoom: {
        name: "Zoom",
        icon: <Video className="h-5 w-5" />,
        color: "blue",
        description: "Réunion vidéo Zoom",
      },
      google_meet: {
        name: "Google Meet",
        icon: <Video className="h-5 w-5" />,
        color: "green",
        description: "Réunion Google Meet",
      },
      discord: {
        name: "Discord",
        icon: <Monitor className="h-5 w-5" />,
        color: "purple",
        description: "Appel Discord",
      },
      teams: {
        name: "Microsoft Teams",
        icon: <Video className="h-5 w-5" />,
        color: "blue",
        description: "Réunion Teams",
      },
      telephonique: {
        name: "Téléphonique",
        icon: <Phone className="h-5 w-5" />,
        color: "gray",
        description: "Entretien téléphonique",
      },
      presentiel: {
        name: "Présentiel",
        icon: <MapPin className="h-5 w-5" />,
        color: "orange",
        description: "Entretien en personne",
      },
      technique: {
        name: "Technique",
        icon: <Monitor className="h-5 w-5" />,
        color: "indigo",
        description: "Test technique",
      },
      visio: {
        name: "Visioconférence",
        icon: <Video className="h-5 w-5" />,
        color: "blue",
        description: "Entretien en visioconférence",
      },
    };
    return details[type] || details.presentiel;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}min`
      : `${hours}h`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copié",
      description: "Le lien a été copié dans le presse-papiers",
    });
  };

  const platformDetails = getPlatformDetails(interview.type_entretien);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Entretien avec {interview.candidature?.prenom}{" "}
            {interview.candidature?.nom}
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {platformDetails.icon}
              <span className="font-medium">{platformDetails.name}</span>
            </div>
            <Badge className={getStatusColor(interview.statut)}>
              {getStatusIcon(interview.statut)}
              <span className="ml-1">{getStatusLabel(interview.statut)}</span>
            </Badge>
            {interview.resultat && (
              <Badge className={getResultatColor(interview.resultat)}>
                {getResultatLabel(interview.resultat)}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Interview Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Détails de l'entretien
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium">
                      {formatDate(interview.date_entretien)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Heure</p>
                    <p className="font-medium">
                      {formatTime(interview.date_entretien)}
                    </p>
                  </div>
                </div>

                {interview.duree && (
                  <div className="flex items-center gap-3">
                    <Timer className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Durée</p>
                      <p className="font-medium">
                        {formatDuration(interview.duree)}
                      </p>
                    </div>
                  </div>
                )}

                {interview.intervieweur && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Intervieweur</p>
                      <p className="font-medium">
                        {interview.intervieweur.prenom}{" "}
                        {interview.intervieweur.nom}
                      </p>
                      {interview.intervieweur.email && (
                        <p className="text-sm text-gray-500">
                          {interview.intervieweur.email}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Location or Meeting Link */}
              {interview.lieu && (
                <div className="border-t pt-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-gray-500 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Lieu</p>
                      <p className="font-medium">{interview.lieu}</p>
                    </div>
                  </div>
                </div>
              )}

              {interview.lien_reunion && (
                <div className="border-t pt-4">
                  <div className="flex items-start gap-3">
                    <Video className="h-4 w-4 text-gray-500 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-2">
                        Lien de réunion
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm flex-1 truncate">
                          {interview.lien_reunion}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            copyToClipboard(interview.lien_reunion)
                          }
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button size="sm" asChild>
                          <a
                            href={interview.lien_reunion}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Rejoindre
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {interview.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap">{interview.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Platform Instructions */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                {platformDetails.icon}
                Instructions pour {platformDetails.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-blue-800">
                {interview.type_entretien === "zoom" && (
                  <div>
                    <p className="mb-2">
                      <strong>Avant l'entretien :</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Assurez-vous d'avoir l'application Zoom installée</li>
                      <li>Testez votre caméra et microphone</li>
                      <li>
                        Rejoignez la réunion 5 minutes avant l'heure prévue
                      </li>
                      <li>Préparez un environnement calme et bien éclairé</li>
                    </ul>
                  </div>
                )}
                {interview.type_entretien === "google_meet" && (
                  <div>
                    <p className="mb-2">
                      <strong>Avant l'entretien :</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Utilisez Chrome pour une meilleure expérience</li>
                      <li>Autorisez l'accès à votre caméra et microphone</li>
                      <li>
                        Cliquez sur le lien 5 minutes avant l'heure prévue
                      </li>
                      <li>Ayez votre compte Google prêt si nécessaire</li>
                    </ul>
                  </div>
                )}
                {interview.type_entretien === "teams" && (
                  <div>
                    <p className="mb-2">
                      <strong>Avant l'entretien :</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>
                        Utilisez l'application Teams ou rejoignez via le
                        navigateur
                      </li>
                      <li>Testez vos périphériques audio/vidéo</li>
                      <li>Rejoignez la réunion quelques minutes en avance</li>
                      <li>Préparez votre compte Microsoft si nécessaire</li>
                    </ul>
                  </div>
                )}
                {interview.type_entretien === "discord" && (
                  <div>
                    <p className="mb-2">
                      <strong>Avant l'entretien :</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Assurez-vous d'avoir un compte Discord</li>
                      <li>Testez votre micro et vos haut-parleurs</li>
                      <li>Rejoignez le serveur ou le canal spécifié</li>
                      <li>Configurez votre statut comme disponible</li>
                    </ul>
                  </div>
                )}
                {interview.type_entretien === "telephonique" && (
                  <div>
                    <p className="mb-2">
                      <strong>Avant l'entretien :</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Vérifiez que votre téléphone est chargé</li>
                      <li>Trouvez un endroit calme avec une bonne réception</li>
                      <li>Ayez un carnet et un stylo à portée de main</li>
                      <li>Préparez les questions que vous souhaitez poser</li>
                    </ul>
                  </div>
                )}
                {interview.type_entretien === "presentiel" && (
                  <div>
                    <p className="mb-2">
                      <strong>Avant l'entretien :</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Planifiez votre trajet à l'avance</li>
                      <li>Arrivez 10-15 minutes en avance</li>
                      <li>Apportez des copies de votre CV</li>
                      <li>Habillez-vous de manière appropriée</li>
                    </ul>
                  </div>
                )}
                {interview.type_entretien === "technique" && (
                  <div>
                    <p className="mb-2">
                      <strong>Avant l'entretien :</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Préparez votre environnement de développement</li>
                      <li>
                        Assurez-vous d'avoir une connexion internet stable
                      </li>
                      <li>Testez les outils de partage d'écran</li>
                      <li>Révisez les concepts techniques pertinents</li>
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Candidate Information */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Candidat
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-semibold text-lg">
                  {interview.candidature?.prenom} {interview.candidature?.nom}
                </p>
                {interview.candidature?.email && (
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {interview.candidature.email}
                    </span>
                  </div>
                )}
              </div>

              <Separator />

              {interview.candidature?.offre && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Poste</p>
                  <p className="font-medium">
                    {interview.candidature.offre.titre}
                  </p>
                  {interview.candidature.offre.departement && (
                    <div className="flex items-center gap-1 mt-1">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {interview.candidature.offre.departement.nom}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {interview.candidature?.statut && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    Statut de candidature
                  </p>
                  <Badge variant="outline">
                    {interview.candidature.statut}
                  </Badge>
                </div>
              )}

              {interview.candidature?.date_candidature && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    Date de candidature
                  </p>
                  <p className="text-sm">
                    {formatDate(interview.candidature.date_candidature)}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {interview.lien_reunion && (
                <Button className="w-full" asChild>
                  <a
                    href={interview.lien_reunion}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Rejoindre la réunion
                  </a>
                </Button>
              )}
              {interview.candidature?.email && (
                <Button variant="outline" className="w-full" asChild>
                  <a href={`mailto:${interview.candidature.email}`}>
                    <Mail className="h-4 w-4 mr-2" />
                    Contacter le candidat
                  </a>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InterviewDetails;
